import { useState, useEffect, useRef, useCallback } from 'react';
import { useBarcodeCartStore, BarcodeCartItem } from '@/stores/barcodeCartStore';
import { mockCompany } from '@/mocks/masters';
import { getData, postData } from '@/services/FetchNodeServices';
import SerialSelectModal from '@/components/feature/barcode-print/SerialSelectModal';
import BatchSelectModal from '@/components/feature/barcode-print/BatchSelectModal';
import { serialApi } from '@/api/serial.api';

declare const JsBarcode: (el: SVGSVGElement | HTMLImageElement, value: string, options?: Record<string, unknown>) => void;

type LabelSize = '50x25' | '75x37' | '100x50' | 'custom';
type BarcodeFormat = 'EAN13' | 'CODE128' | 'per-item';

interface LabelSettings {
  size: LabelSize;
  customW: number;
  customH: number;
  format: BarcodeFormat;
  fields: {
    companyName: boolean;
    productName: boolean;
    brand: boolean;
    category: boolean;
    mrp: boolean;
    saleRate: boolean;
    hsnCode: boolean;
    articleNo: boolean;
    barcodeImage: boolean;
    barcodeNumber: boolean;
    sizeColor: boolean;
    customText: boolean;
    serialNumber: boolean;
    batchNumber: boolean;
    expiryDate: boolean;
  };
  customText: string;
}

const SIZE_MAP: Record<LabelSize, { w: number; h: number; label: string }> = {
  '50x25': { w: 50, h: 25, label: '50×25mm Small' },
  '75x37': { w: 75, h: 37, label: '75×37mm Medium' },
  '100x50': { w: 100, h: 50, label: '100×50mm Large' },
  custom: { w: 0, h: 0, label: 'Custom' },
};

const defaultSettings: LabelSettings = {
  size: '75x37',
  customW: 80,
  customH: 40,
  format: 'CODE128',
  fields: {
    companyName: true,
    productName: true,
    brand: true,
    category: false,
    mrp: true,
    saleRate: true,
    hsnCode: false,
    articleNo: false,
    barcodeImage: true,
    barcodeNumber: true,
    sizeColor: false,
    customText: false,
    serialNumber: true,
    batchNumber: true,
    expiryDate: false,
  },
  customText: '',
};

// ─── Label Preview ───────────────────────────────────────────────────────────
interface LabelPreviewProps {
  settings: LabelSettings;
  item: BarcodeCartItem;
}

function LabelPreview({ settings, item }: LabelPreviewProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { w, h } = settings.size === 'custom'
    ? { w: settings.customW, h: settings.customH }
    : SIZE_MAP[settings.size];

  const barcodeVal = item.barcode || '0000000000000';

  useEffect(() => {
    if (!svgRef.current || !settings.fields.barcodeImage) return;
    try {
      const fmt = settings.format === 'per-item' ? 'CODE128' : settings.format;
      JsBarcode(svgRef.current, barcodeVal, {
        format: fmt === 'EAN13' ? 'EAN13' : 'CODE128',
        width: 1.2,
        height: 28,
        displayValue: false,
        margin: 0,
      });
    } catch {
      // invalid barcode for format
    }
  }, [settings.fields.barcodeImage, settings.format, barcodeVal]);

  const scale = 3.2;

  return (
    <div
      className="border-2 border-dashed border-slate-300 bg-white rounded overflow-hidden mx-auto shadow-sm"
      style={{ width: w * scale, height: h * scale, minWidth: 120, minHeight: 60 }}
    >
      <div className="flex flex-col h-full px-1.5 py-1 text-[8px] leading-tight">
        {settings.fields.companyName && (
          <div className="font-bold text-slate-800 truncate" style={{ fontSize: 7 }}>
            {mockCompany.name}
          </div>
        )}
        {settings.fields.productName && (
          <div className="font-semibold text-slate-700 truncate" style={{ fontSize: 7.5 }}>
            {item.name}
          </div>
        )}
        {settings.fields.brand && (
          <div className="text-slate-500 truncate" style={{ fontSize: 6.5 }}>
            {item.brand}
          </div>
        )}
        {settings.fields.category && (
          <div className="text-slate-400 truncate" style={{ fontSize: 6 }}>
            {item.category}
          </div>
        )}
        <div className="flex items-center gap-2 mt-auto">
          {settings.fields.mrp && (
            <span className="text-slate-700 font-semibold" style={{ fontSize: 7 }}>
              MRP: ₹{item.mrp}
            </span>
          )}
          {settings.fields.saleRate && (
            <span className="text-indigo-700 font-bold" style={{ fontSize: 7 }}>
              ₹{item.saleRate}
            </span>
          )}
        </div>

        {/* Serial & Batch info */}
        {settings.fields.serialNumber && item.serialNumber && (
          <div className="font-mono font-bold text-indigo-900 truncate" style={{ fontSize: 6.5 }}>
            SN: {item.serialNumber}
          </div>
        )}
        {settings.fields.batchNumber && item.batchNumber && (
          <div className="font-mono font-semibold text-emerald-800 truncate" style={{ fontSize: 6.5 }}>
            Batch: {item.batchNumber}
          </div>
        )}
        {settings.fields.expiryDate && item.expiryDate && (
          <div className="text-slate-500 truncate" style={{ fontSize: 6 }}>
            Exp: {item.expiryDate}
          </div>
        )}

        {settings.fields.barcodeImage && (
          <svg ref={svgRef} className="w-full mt-0.5" style={{ maxHeight: 28 }} />
        )}
        {settings.fields.barcodeNumber && (
          <div className="text-center font-mono text-slate-600" style={{ fontSize: 6 }}>
            {barcodeVal}
          </div>
        )}
        {settings.fields.customText && settings.customText && (
          <div className="text-slate-500 truncate" style={{ fontSize: 6 }}>
            {settings.customText}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
interface Props {
  onClose: () => void;
}

export default function LabelDesignerModal({ onClose }: Props) {
  const { items, updateQty, setItems, removeFromCart } = useBarcodeCartStore();
  const [settings, setSettings] = useState<LabelSettings>(defaultSettings);
  const [rememberSettings, setRememberSettings] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Modals state
  const [serialModalItem, setSerialModalItem] = useState<{ item: BarcodeCartItem; index: number } | null>(null);
  const [batchModalItem, setBatchModalItem] = useState<{ item: BarcodeCartItem; index: number } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Settings loading/saving state
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const previewItem: BarcodeCartItem = items[previewIndex] ?? items[0] ?? {
    itemId: 'preview',
    name: 'Sample Product Name',
    brand: 'Brand',
    category: 'Category',
    barcode: '7612345678901',
    mrp: 1499,
    saleRate: 1200,
    labelQty: 1,
  };

  const totalLabels = items.reduce((s, i) => s + i.labelQty, 0);

  // Load settings from backend on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setSettingsLoading(true);
        const response = await getData('api/label-settings/get-label-settings');
        if (response?.success && response?.settings) {
          setSettings({
            ...defaultSettings,
            ...response.settings,
            fields: { ...defaultSettings.fields, ...(response.settings.fields || {}) },
          });
        }
      } catch (err) {
        console.error('Failed to load label settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Save settings to backend
  const saveSettingsToBackend = useCallback(async (settingsToSave: LabelSettings) => {
    try {
      setSaving(true);
      setSaveStatus('idle');
      const response = await postData('api/label-settings/save-label-settings', {
        settings: settingsToSave,
      });
      if (response?.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      console.error('Failed to save label settings:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, []);

  const setField = (key: keyof LabelSettings['fields'], val: boolean) => {
    setSettings((prev) => ({ ...prev, fields: { ...prev.fields, [key]: val } }));
  };

  const { w, h } = settings.size === 'custom'
    ? { w: settings.customW, h: settings.customH }
    : SIZE_MAP[settings.size];

  const buildLabelHTML = useCallback((item: BarcodeCartItem): string => {
    const fmt = settings.format === 'per-item' ? 'CODE128' : settings.format;
    const barcodeId = `bc-${item.itemId}-${Math.random().toString(36).slice(2)}`;
    const barcodeVal = item.barcode || '0000000000000';

    const rows: string[] = [];
    if (settings.fields.companyName) rows.push(`<div style="font-weight:700;font-size:7pt;overflow:hidden;white-space:nowrap;">${mockCompany.name}</div>`);
    if (settings.fields.productName) rows.push(`<div style="font-weight:600;font-size:8pt;overflow:hidden;white-space:nowrap;">${item.name}</div>`);
    if (settings.fields.brand) rows.push(`<div style="font-size:6.5pt;color:#555;overflow:hidden;white-space:nowrap;">${item.brand}</div>`);
    if (settings.fields.category) rows.push(`<div style="font-size:6pt;color:#888;overflow:hidden;white-space:nowrap;">${item.category}</div>`);

    const priceRow: string[] = [];
    if (settings.fields.mrp) priceRow.push(`<span style="font-size:7pt;font-weight:600;">MRP: &#8377;${item.mrp}</span>`);
    if (settings.fields.saleRate) priceRow.push(`<span style="font-size:7pt;font-weight:700;color:#4f46e5;">&#8377;${item.saleRate}</span>`);
    if (priceRow.length) rows.push(`<div style="display:flex;gap:8px;margin-top:auto;">${priceRow.join('')}</div>`);

    // Serial, Batch, and Expiry Text Rows
    if (settings.fields.serialNumber && item.serialNumber)
      rows.push(`<div style="font-family:monospace;font-weight:700;font-size:6.5pt;color:#1e1b4b;overflow:hidden;white-space:nowrap;">SN: ${item.serialNumber}</div>`);
    if (settings.fields.batchNumber && item.batchNumber)
      rows.push(`<div style="font-family:monospace;font-size:6.5pt;color:#064e3b;overflow:hidden;white-space:nowrap;">Batch: ${item.batchNumber}</div>`);
    if (settings.fields.expiryDate && item.expiryDate)
      rows.push(`<div style="font-size:6pt;color:#555;overflow:hidden;white-space:nowrap;">Exp: ${item.expiryDate}</div>`);

    if (settings.fields.barcodeImage) {
      rows.push(`<svg id="${barcodeId}" style="width:100%;max-height:28px;"></svg>`);
    }
    if (settings.fields.barcodeNumber) {
      rows.push(`<div style="text-align:center;font-family:monospace;font-size:6pt;color:#444;">${barcodeVal}</div>`);
    }
    if (settings.fields.customText && settings.customText) {
      rows.push(`<div style="font-size:6pt;color:#666;overflow:hidden;white-space:nowrap;">${settings.customText}</div>`);
    }

    const initScript = settings.fields.barcodeImage
      ? `<script>
          (function() {
            var el = document.getElementById('${barcodeId}');
            if (el && typeof JsBarcode !== 'undefined') {
              try {
                JsBarcode(el, '${barcodeVal}', {
                  format: '${fmt === 'EAN13' ? 'EAN13' : 'CODE128'}',
                  width: 1.2, height: 28, displayValue: false, margin: 0
                });
              } catch(e) {}
            }
          })();
        <\/script>`
      : '';

    return `
      <div class="label" style="width:${w}mm;height:${h}mm;box-sizing:border-box;padding:2mm;border:1px solid #000;display:flex;flex-direction:column;overflow:hidden;page-break-inside:avoid;break-inside:avoid;background:white;color:black;">
        ${rows.join('')}
        ${initScript}
      </div>`;
  }, [settings, w, h]);

  const handlePrint = () => {
    if (items.length === 0) return;
    setPrinting(true);

    if (rememberSettings) {
      saveSettingsToBackend(settings);
    }

    const labelsHTML = items.flatMap((item) =>
      Array.from({ length: item.labelQty }, () => buildLabelHTML(item))
    ).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Barcode Labels</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; color-adjust: exact; }
    html, body { background: white !important; color: black !important; font-family: Arial, sans-serif; }
    .labels-grid { display: flex; flex-wrap: wrap; gap: 2mm; padding: 5mm; background: white; }
    .label { page-break-inside: avoid; break-inside: avoid; background: white !important; color: black !important; border: 1px solid #000 !important; }
    svg { fill: black; }
    @media print {
      html, body { margin: 0; background: white !important; color: black !important; }
      .labels-grid { padding: 3mm; gap: 1mm; }
      .label { background: white !important; color: black !important; border: 1px solid #000 !important; }
    }
  </style>
</head>
<body>
  <div class="labels-grid">
    ${labelsHTML}
  </div>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() { window.close(); }, 1000);
    };
  <\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
    setPrinting(false);
  };

  // Handler for applying serials
  const handleApplySerials = (serials: string[]) => {
    if (!serialModalItem) return;
    const { item, index } = serialModalItem;

    if (serials.length === 0) {
      const updated = [...items];
      updated[index] = { ...item, serialNumber: undefined, labelQty: item.labelQty || 1 };
      setItems(updated);
      return;
    }

    if (serials.length === 1) {
      const updated = [...items];
      updated[index] = { ...item, serialNumber: serials[0], labelQty: 1 };
      setItems(updated);
      return;
    }

    // Bulk: expand into N unique labels (1 per serial)
    const newItems: BarcodeCartItem[] = serials.map((sn, idx) => ({
      ...item,
      rowKey: `${item.itemId}_${sn}_${idx}`,
      serialNumber: sn,
      labelQty: 1,
    }));

    const updated = [...items];
    updated.splice(index, 1, ...newItems);
    setItems(updated);
  };

  // Handler for applying batch
  const handleApplyBatch = (batchNumber: string, labelQty: number, expiryDate?: string, mfgDate?: string) => {
    if (!batchModalItem) return;
    const { item, index } = batchModalItem;

    const updated = [...items];
    updated[index] = {
      ...item,
      batchNumber,
      expiryDate,
      mfgDate,
      labelQty,
    };
    setItems(updated);
  };

  // Auto-Select available serials for all serial-tracked items
  const handleAutoSelectAllSerials = async () => {
    try {
      setBulkLoading(true);
      const nextItems: BarcodeCartItem[] = [];

      for (const item of items) {
        if (item.isSerialTracked && !item.serialNumber) {
          const res = await serialApi.getAvailable(item.itemId);
          if (res.success && Array.isArray(res.data) && res.data.length > 0) {
            const serials = res.data.slice(0, item.labelQty || res.data.length);
            serials.forEach((sn, idx) => {
              nextItems.push({
                ...item,
                rowKey: `${item.itemId}_${sn.serial_number}_${idx}`,
                serialNumber: sn.serial_number,
                labelQty: 1,
              });
            });
            continue;
          }
        }
        nextItems.push(item);
      }

      setItems(nextItems);
    } catch (err) {
      console.error('Failed to auto-select serials:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const hasSerialTrackedItems = items.some((i) => i.isSerialTracked);

  // Show loading spinner while settings are being fetched
  if (settingsLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-xl px-10 py-8 flex flex-col items-center gap-3 shadow-xl">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading label settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 rounded-lg text-indigo-600">
              <i className="ri-layout-2-line text-lg" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800 text-base">Label Designer</h2>
              <p className="text-xs text-slate-400">{items.length} items · {totalLabels} total labels</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Modal body */}
        <div className="flex flex-1 overflow-hidden">

          {/* LEFT — Settings */}
          <div className="w-80 shrink-0 border-r border-slate-100 overflow-y-auto p-5 space-y-5">

            {/* Label Size */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Label Size</p>
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {(['50x25', '75x37', '100x50', 'custom'] as LabelSize[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSettings((p) => ({ ...p, size: s }))}
                    className={`text-xs px-2 py-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                      settings.size === s
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    {SIZE_MAP[s].label}
                  </button>
                ))}
              </div>
              {settings.size === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Width (mm)</label>
                    <input
                      type="number"
                      value={settings.customW}
                      onChange={(e) => setSettings((p) => ({ ...p, customW: parseInt(e.target.value) || 80 }))}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Height (mm)</label>
                    <input
                      type="number"
                      value={settings.customH}
                      onChange={(e) => setSettings((p) => ({ ...p, customH: parseInt(e.target.value) || 40 }))}
                      className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-indigo-400"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Barcode Format */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Barcode Format</p>
              <div className="space-y-1.5">
                {([
                  { val: 'CODE128', label: 'Code128 · Alphanumeric' },
                  { val: 'EAN13', label: 'EAN-13 · 13 digits' },
                ] as { val: BarcodeFormat; label: string }[]).map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => setSettings((p) => ({ ...p, format: val }))}
                    className={`w-full text-left text-xs px-3 py-2 rounded-md border transition-colors cursor-pointer ${
                      settings.format === val
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-indigo-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Fields to Print */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fields to Print</p>
              <div className="grid grid-cols-2 gap-1.5">
                {([
                  { key: 'companyName', label: 'Company Name' },
                  { key: 'productName', label: 'Product Name' },
                  { key: 'brand', label: 'Brand' },
                  { key: 'category', label: 'Category' },
                  { key: 'mrp', label: 'MRP' },
                  { key: 'saleRate', label: 'Sale Rate' },
                  { key: 'serialNumber', label: 'Serial No. (SN)' },
                  { key: 'batchNumber', label: 'Batch No.' },
                  { key: 'expiryDate', label: 'Expiry Date' },
                  { key: 'hsnCode', label: 'HSN Code' },
                  { key: 'articleNo', label: 'Article No' },
                  { key: 'barcodeImage', label: 'Barcode Image' },
                  { key: 'barcodeNumber', label: 'Barcode Number' },
                  { key: 'sizeColor', label: 'Size / Color' },
                  { key: 'customText', label: 'Custom Text' },
                ] as { key: keyof LabelSettings['fields']; label: string }[]).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={settings.fields[key]}
                      onChange={(e) => setField(key, e.target.checked)}
                      className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer"
                    />
                    <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors">{label}</span>
                  </label>
                ))}
              </div>
              {settings.fields.customText && (
                <input
                  type="text"
                  placeholder="Enter custom text..."
                  value={settings.customText}
                  onChange={(e) => setSettings((p) => ({ ...p, customText: e.target.value }))}
                  className="mt-2 w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm outline-none focus:border-indigo-400"
                />
              )}
            </div>

            {/* Remember Settings — saves to backend */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberSettings}
                onChange={(e) => setRememberSettings(e.target.checked)}
                className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer"
              />
              <span className="text-xs text-slate-600">Remember settings for next time</span>
            </label>

            {/* Manual Save Settings button */}
            <button
              onClick={() => saveSettingsToBackend(settings)}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {saving ? (
                <>
                  <div className="w-3 h-3 border border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : saveStatus === 'saved' ? (
                <>
                  <i className="ri-check-line text-green-500" />
                  <span className="text-green-600">Saved!</span>
                </>
              ) : saveStatus === 'error' ? (
                <>
                  <i className="ri-error-warning-line text-red-500" />
                  <span className="text-red-500">Save failed</span>
                </>
              ) : (
                <>
                  <i className="ri-save-line" />
                  Save Settings
                </>
              )}
            </button>
          </div>

          {/* RIGHT — Preview + Quantities */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Live Preview</p>
                {previewItem && (
                  <span className="text-xs text-indigo-600 font-medium">
                    Previewing: {previewItem.name}{' '}
                    {previewItem.serialNumber && `(SN: ${previewItem.serialNumber})`}
                  </span>
                )}
              </div>
              <div className="flex flex-col items-center gap-3">
                <LabelPreview settings={settings} item={previewItem} />
                <p className="text-xs text-slate-400">
                  Preview: {w}mm × {h}mm · {settings.format === 'EAN13' ? 'EAN-13' : settings.format === 'CODE128' ? 'Code128' : 'Per Item'}
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Label Quantities</p>

                {hasSerialTrackedItems && (
                  <button
                    onClick={handleAutoSelectAllSerials}
                    disabled={bulkLoading}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold border border-indigo-200 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {bulkLoading ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-magic-line text-indigo-600" />
                    )}
                    Auto-Select Available Serials
                  </button>
                )}
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="w-8 px-3 py-2.5 text-center text-xs font-semibold text-slate-500">View</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Item & Tracking Details</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Labels Qty</th>
                      <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => {
                      const isSelectedPreview = previewIndex === index;
                      const itemKey = item.rowKey || `${item.itemId}_${item.serialNumber || 'default'}_${index}`;
                      return (
                        <tr
                          key={itemKey}
                          onClick={() => setPreviewIndex(index)}
                          className={`cursor-pointer transition-colors ${
                            isSelectedPreview ? 'bg-indigo-50/40' : 'hover:bg-slate-50/70'
                          } ${!item.barcode ? 'opacity-60' : ''}`}
                        >
                          <td className="px-3 py-2.5 text-center">
                            <input
                              type="radio"
                              name="designer-preview-radio"
                              checked={isSelectedPreview}
                              onChange={() => setPreviewIndex(index)}
                              className="accent-indigo-600 cursor-pointer"
                            />
                          </td>

                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800 text-sm">{item.name}</span>
                              {item.isSerialTracked && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-50 text-indigo-700 rounded border border-indigo-200">
                                  Serial
                                </span>
                              )}
                              {item.isBatchTracked && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 rounded border border-emerald-200">
                                  Batch
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-3 mt-1 text-xs">
                              {item.barcode ? (
                                <span className="text-slate-400 font-mono text-[11px]">{item.barcode}</span>
                              ) : (
                                <span className="text-amber-500 text-[11px]">No barcode</span>
                              )}

                              {/* Serial Number Selector / Display */}
                              {(item.isSerialTracked || item.serialNumber) && (
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  {item.serialNumber ? (
                                    <span className="inline-flex items-center gap-1 font-mono font-bold text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200">
                                      SN: {item.serialNumber}
                                      <button
                                        onClick={() => setSerialModalItem({ item, index })}
                                        title="Change Serial"
                                        className="hover:text-indigo-900 ml-0.5"
                                      >
                                        <i className="ri-pencil-line text-[11px]" />
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => setSerialModalItem({ item, index })}
                                      className="px-2 py-0.5 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded flex items-center gap-1 cursor-pointer transition-colors"
                                    >
                                      <i className="ri-barcode-line" /> Select Serials
                                    </button>
                                  )}
                                </div>
                              )}

                              {/* Batch Number Selector / Display */}
                              {(item.isBatchTracked || item.batchNumber) && (
                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  {item.batchNumber ? (
                                    <span className="inline-flex items-center gap-1 font-mono font-semibold text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">
                                      Batch: {item.batchNumber}
                                      {item.expiryDate && (
                                        <span className="text-[10px] text-emerald-600 font-normal">
                                          (Exp: {item.expiryDate})
                                        </span>
                                      )}
                                      <button
                                        onClick={() => setBatchModalItem({ item, index })}
                                        title="Change Batch"
                                        className="hover:text-emerald-900 ml-0.5"
                                      >
                                        <i className="ri-pencil-line text-[11px]" />
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => setBatchModalItem({ item, index })}
                                      className="px-2 py-0.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded flex items-center gap-1 cursor-pointer transition-colors"
                                    >
                                      <i className="ri-archive-line" /> Select Batch
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min={1}
                              value={item.labelQty}
                              onChange={(e) => updateQty(item.itemId, parseInt(e.target.value) || 1)}
                              disabled={!item.barcode || Boolean(item.serialNumber)}
                              title={item.serialNumber ? 'Quantity is fixed to 1 per unique serial number' : undefined}
                              className="w-16 text-center border border-slate-200 rounded-md py-1 text-sm font-semibold outline-none focus:border-indigo-400 disabled:bg-slate-50 disabled:text-slate-400"
                            />
                          </td>

                          <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => removeFromCart(item.itemId)}
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                              title="Remove item"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-right text-xs text-slate-500">
                  Total labels: <span className="font-bold text-slate-800 text-sm">{totalLabels}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={printing || items.length === 0}
            className="flex items-center gap-2 px-5 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap shadow-sm"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-printer-line text-sm" />
            </div>
            Print Labels
          </button>
        </div>
      </div>

      {/* Serial Selection Modal */}
      {serialModalItem && (
        <SerialSelectModal
          open={Boolean(serialModalItem)}
          onClose={() => setSerialModalItem(null)}
          itemId={serialModalItem.item.itemId}
          itemName={serialModalItem.item.name}
          selectedSerials={
            serialModalItem.item.serialNumber ? [serialModalItem.item.serialNumber] : []
          }
          onApply={handleApplySerials}
        />
      )}

      {/* Batch Selection Modal */}
      {batchModalItem && (
        <BatchSelectModal
          open={Boolean(batchModalItem)}
          onClose={() => setBatchModalItem(null)}
          itemId={batchModalItem.item.itemId}
          itemName={batchModalItem.item.name}
          currentBatchNumber={batchModalItem.item.batchNumber}
          currentLabelQty={batchModalItem.item.labelQty}
          onApply={handleApplyBatch}
        />
      )}
    </div>
  );
}